import { useCallback } from 'react';

import * as env from 'src/lib/env';
import {
  isAbortError,
  isAsyncRequestError,
  isBackendError,
  isChannelAuthError,
  isExtractionError,
  isJobError,
  isUploadError,
  isValidationError,
} from 'src/lib/errors';
import { captureException, captureMessage } from 'src/lib/sentry';
import { useNotificationActions } from 'src/store';

const shouldDisplayAsWarning = (error) => isValidationError(error);

const shouldDisplayAsIs = (error) =>
  error?.networkError ||
  error?.graphQLErrors ||
  isBackendError(error) ||
  isChannelAuthError(error) ||
  isAsyncRequestError(error) ||
  isExtractionError(error) ||
  isUploadError(error) ||
  isJobError(error);

export const useErrorHandler = () => {
  const notificationActions = useNotificationActions();

  return useCallback(
    (error, extra) => {
      // Generally we should not have "AbortErrors" here, as they should be handled closer to the source
      if (isAbortError(error)) {
        captureMessage('"AbortError" was propagated to the generic error handler', {
          level: 'warning',
          extra: {
            error: error.toString(),
            stack: error.stack,
            source: 'useErrorHandler',
          },
        });
        return;
      }

      if (env.getAppEnv() === 'development') {
        // eslint-disable-next-line no-console
        console.error('[useErrorHandler]', error.message);
      }

      if (shouldDisplayAsWarning(error) && error.message) {
        notificationActions.displayWarning(error.message);
      } else if (shouldDisplayAsIs(error) && error.message) {
        notificationActions.displayError(error);
      } else {
        notificationActions.displayError(
          new Error('Sorry, something went wrong. We’re working on it.')
        );

        captureException(error, {
          extra: {
            source: 'useErrorHandler',
            ...extra,
          },
        });
      }
    },
    [notificationActions]
  );
};
